import * as PropTypes from 'prop-types';
import styled from 'styled-components';

const PaginationWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const PaginationButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border: 1px solid ${(props) => props.theme.stroke};
  border-radius: 0.25rem;
  overflow: hidden;
`;

const PaginationButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  font-weight: 600;
  font-size: 0.75rem;
  line-height: 1.5rem;
  letter-spacing: 0.03em;
  border: 0;
  color: ${(props) =>
    props.active ? props.theme.noContrast : props.theme.secondaryText};
  background-color: ${(props) =>
    props.active ? props.theme.primary : props.theme.noContrast};
  cursor: pointer;
  transition: 0.225s all;
  &:hover {
    background-color: ${(props) =>
      props.active ? props.theme.primaryHover : props.theme.noContrastHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function Pagination({ onNext, onPrevious, onPageClick, metadata }) {
  const hasNext = metadata.limit + metadata.offset < metadata.count;
  const hasPrevious = metadata.offset > 0;
  const totalPages = Math.ceil(metadata.count / metadata.limit) || 1;
  const currentPage =
    metadata.offset >= metadata.count ? 1 : metadata.offset / metadata.limit;

  /**
   * Calculate previous offset and pass to handler.
   */
  function handlePrevious(e) {
    e.preventDefault();
    onPrevious({
      limit: metadata.limit,
      offset: metadata.offset - metadata.limit,
    });
  }

  /**
   * Calculate next offset and pass to handler.
   */
  function handleNext(e) {
    e.preventDefault();
    onNext({
      limit: metadata.limit,
      offset: metadata.offset + metadata.limit,
    });
  }

  /**
   * Calculate next offset based on selected page (zero-based) and pass to handler.
   */
  function handlePageClick(e, index) {
    e.preventDefault();
    onPageClick(
      {
        limit: metadata.limit,
        offset: index * metadata.limit,
      },
      index + 1
    );
  }

  /**
   * Check which page is active
   */
  function isActive(index) {
    return index === currentPage;
  }

  return (
    <PaginationWrapper>
      <PaginationButtons>
        {totalPages > 1 && (
          <PaginationButton onClick={handlePrevious} disabled={!hasPrevious}>
            <span className="material-icons">chevron_left</span>
          </PaginationButton>
        )}
        {[...Array(totalPages)].map((_, i) => (
          <PaginationButton
            key={i}
            active={isActive(i)}
            onClick={(e) => handlePageClick(e, i)}
          >
            {i + 1}
          </PaginationButton>
        ))}
        {totalPages > 1 && (
          <PaginationButton onClick={handleNext} disabled={!hasNext}>
            <span className="material-icons">chevron_right</span>
          </PaginationButton>
        )}
      </PaginationButtons>
    </PaginationWrapper>
  );
}

Pagination.propTypes = {
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  onPageClick: PropTypes.func,
  metadata: PropTypes.object,
};

Pagination.defaultProps = {
  onNext: () => null,
  onPrevious: () => null,
  onPageClick: () => null,
  metadata: {
    count: 0,
    limit: 30,
    offset: 0,
  },
};

export default Pagination;
